import {
  Button,
  Checkbox,
  cn,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@1trading/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { Building2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { Controller, type DefaultValues, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { confirmAction } from '@/components/dialogs/confirm-action';
import { FieldError } from '@/components/ui/field-error';
import { JURISDICTIONS_QUERY_KEY } from '@/hooks/queries/compliance/use-jurisdictions';
import { getErrorMessage } from '@/lib/api/error';
import {
  AML_RISK_OPTIONS,
  addJurisdiction,
  deleteJurisdiction,
  editJurisdiction,
  type Jurisdiction,
  POSTAL_CODE_USE_OPTIONS,
  PRODUCT_TYPES,
  REQUIRED_DOCUMENT_ID_OPTIONS,
  repackJurisdiction,
} from '@/services/api/jurisdictions';
import { JURISDICTION_COLUMNS, type JurisdictionColumnConfig } from './jurisdiction-columns';

const jurisdictionFormSchema = z.object({
  name: z.string().min(1, 'Jurisdiction is required'),
  code: z.string().min(1, 'Alpha 2 Code is required').length(2, 'Must be 2 characters'),
  alpha3: z.string().min(1, 'Alpha 3 Code is required').length(3, 'Must be 3 characters'),
  amlRisk: z.enum(AML_RISK_OPTIONS, { error: 'Risk Level is required' }),
  phoneCode: z.string().min(1, 'Phone Code is required'),
  name_de: z.string().min(1, 'German Name is required'),
  requiredDocumentIds: z.array(z.enum(REQUIRED_DOCUMENT_ID_OPTIONS)),
  allowedResidency: z.boolean(),
  allowedCitizenship: z.boolean(),
  allowedFiatTransfers: z.boolean(),
  eea: z.boolean(),
  eu: z.boolean(),
  postalCodeUse: z.enum(POSTAL_CODE_USE_OPTIONS, { error: 'Postal Code Use is required' }),
  allowedProductsRetail: z.array(z.enum(PRODUCT_TYPES)),
  allowedProductsProfessional: z.array(z.enum(PRODUCT_TYPES)),
});

type JurisdictionFormSchema = z.infer<typeof jurisdictionFormSchema>;

// Most restrictive add-mode defaults: nothing tradeable until explicitly set.
// amlRisk is deliberately absent — the required select starts empty, blocking Save.
const ADD_MODE_DEFAULTS: DefaultValues<JurisdictionFormSchema> = {
  name: '',
  code: '',
  alpha3: '',
  phoneCode: '',
  name_de: '',
  requiredDocumentIds: [],
  allowedCitizenship: true,
  allowedFiatTransfers: true,
  allowedResidency: true,
  eea: false,
  eu: false,
  postalCodeUse: 'required',
  allowedProductsRetail: [],
  allowedProductsProfessional: [],
};

const toFormValues = (row: Jurisdiction): JurisdictionFormSchema => ({
  name: row.name,
  code: row.code,
  alpha3: row.alpha3,
  phoneCode: row.phoneCode,
  name_de: row.name_de,
  amlRisk: row.amlRisk,
  postalCodeUse: row.postalCodeUse,
  requiredDocumentIds: [...row.requiredDocumentIds],
  allowedResidency: row.allowedResidency,
  allowedCitizenship: row.allowedCitizenship,
  allowedFiatTransfers: row.allowedFiatTransfers,
  eea: row.eea,
  eu: row.eu,
  allowedProductsRetail: [...row.allowedProductsRetail],
  allowedProductsProfessional: [...row.allowedProductsProfessional],
});

// The form is generated from the table column config: every column with a title,
// i.e. everything except the blank-titled flagUrl column
const FORM_FIELDS = JURISDICTION_COLUMNS.filter((config) => config.title !== '');

const BOOLEAN_FIELDS = ['allowedResidency', 'allowedCitizenship', 'allowedFiatTransfers', 'eea', 'eu'] as const;
type BooleanFieldName = (typeof BOOLEAN_FIELDS)[number];

const TEXT_FIELDS = ['name', 'code', 'alpha3', 'phoneCode', 'name_de'] as const;
type TextFieldName = (typeof TEXT_FIELDS)[number];

const SINGLE_SELECT_ITEMS = { amlRisk: AML_RISK_OPTIONS, postalCodeUse: POSTAL_CODE_USE_OPTIONS } as const;
type SingleSelectFieldName = keyof typeof SINGLE_SELECT_ITEMS;

const MULTI_SELECT_ITEMS = {
  requiredDocumentIds: REQUIRED_DOCUMENT_ID_OPTIONS,
  allowedProductsRetail: PRODUCT_TYPES,
  allowedProductsProfessional: PRODUCT_TYPES,
} as const;
type MultiSelectFieldName = keyof typeof MULTI_SELECT_ITEMS;

const TEXT_MAX_LENGTHS: Partial<Record<TextFieldName, number>> = { code: 2, alpha3: 3 };

const isBooleanField = (key: keyof Jurisdiction): key is BooleanFieldName => (BOOLEAN_FIELDS as readonly string[]).includes(key);
const isTextField = (key: keyof Jurisdiction): key is TextFieldName => (TEXT_FIELDS as readonly string[]).includes(key);
const isSingleSelectField = (key: keyof Jurisdiction): key is SingleSelectFieldName => key in SINGLE_SELECT_ITEMS;
const isMultiSelectField = (key: keyof Jurisdiction): key is MultiSelectFieldName => key in MULTI_SELECT_ITEMS;

interface JurisdictionFormDialogProps {
  /** null → add mode */
  jurisdiction: Jurisdiction | null;
  onClose: () => void;
}

export const JurisdictionFormDialog = ({ jurisdiction, onClose }: JurisdictionFormDialogProps) => {
  const queryClient = useQueryClient();
  const isEditMode = jurisdiction !== null;

  const form = useForm<JurisdictionFormSchema>({
    resolver: zodResolver(jurisdictionFormSchema),
    mode: 'onChange',
    defaultValues: isEditMode ? toFormValues(jurisdiction) : ADD_MODE_DEFAULTS,
  });

  const invalidateJurisdictions = () => queryClient.invalidateQueries({ queryKey: JURISDICTIONS_QUERY_KEY });

  const handleSave = form.handleSubmit(async (values) => {
    const actionVerb = isEditMode ? 'Edit' : 'Add';
    const justification = await confirmAction({
      title: `Confirm ${actionVerb} ${values.code} Jurisdiction`,
      // Legacy always said "will add" here even when editing — wording fixed to match the action
      description: `Proceeding with this action will ${actionVerb.toLowerCase()} the ${values.code} Jurisdiction. Your action here will be logged and will be auditable.`,
      actionLabel: `${actionVerb} ${values.code} Jurisdiction`,
      onConfirm: async (comment) => {
        try {
          const body = repackJurisdiction(values, comment);
          await (isEditMode ? editJurisdiction(body) : addJurisdiction(body));
        } catch (error) {
          // Legacy red-snackbar copy; the rethrow keeps the audit dialog open with the upstream error
          throw new Error(`Jurisdiction failed to save, ${getErrorMessage(error)}`);
        }
      },
    });
    if (justification === null) return; // cancelled — form dialog stays open
    toast.success(isEditMode ? 'Jurisdiction updated successfully!' : 'Jurisdiction saved successfully!');
    await invalidateJurisdictions();
    onClose();
  });

  const handleDelete = async () => {
    if (!jurisdiction) return;
    const code = jurisdiction.code;
    const justification = await confirmAction({
      title: `Confirm Delete ${code} Jurisdiction`,
      description: `Proceeding with this action will delete the ${code} Jurisdiction. This action is permanent and not reversible. Your action here will be logged and will be auditable.`,
      actionLabel: `Delete ${code} Jurisdiction`,
      destructive: true,
      // Only { code, comment } reach bop-api
      onConfirm: (comment) => deleteJurisdiction({ code, comment }),
    });
    if (justification === null) return;
    // Legacy gave no delete feedback and left its processing flag stale — tidied here
    toast.success('Jurisdiction deleted successfully!');
    await invalidateJurisdictions();
    onClose();
  };

  const renderField = (config: JurisdictionColumnConfig): ReactNode => {
    const { key, title } = config;
    const fieldId = `jurisdiction-field-${key}`;

    if (isBooleanField(key)) {
      return (
        <Controller
          control={form.control}
          name={key}
          render={({ field }) => (
            <label className="flex h-full items-center gap-2 pt-5 text-sm font-medium" htmlFor={fieldId}>
              <Checkbox checked={field.value} id={fieldId} onCheckedChange={(checked) => field.onChange(checked === true)} />
              {title}
            </label>
          )}
        />
      );
    }

    if (isSingleSelectField(key)) {
      const items: readonly string[] = SINGLE_SELECT_ITEMS[key];
      return (
        <Controller
          control={form.control}
          name={key}
          render={({ field, fieldState }) => (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={fieldId}>{title}</Label>
              <Select onValueChange={field.onChange} value={field.value ?? ''}>
                <SelectTrigger id={fieldId}>
                  <SelectValue placeholder={`Select ${title.toLowerCase()}`} />
                </SelectTrigger>
                <SelectContent>
                  {items.map((item) => (
                    <SelectItem key={item} value={item}>
                      {item}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FieldError message={fieldState.error?.message} />
            </div>
          )}
        />
      );
    }

    if (isMultiSelectField(key)) {
      const items: readonly string[] = MULTI_SELECT_ITEMS[key];
      return (
        <Controller
          control={form.control}
          name={key}
          render={({ field }) => {
            const selected: readonly string[] = field.value;
            return (
              <div className="flex flex-col gap-1.5">
                <Label>{title}</Label>
                <div className="flex min-h-9 flex-wrap items-center gap-x-4 gap-y-2 rounded-md border px-3 py-2">
                  {items.map((item) => (
                    <label className="flex items-center gap-1.5 text-sm" htmlFor={`${fieldId}-${item}`} key={item}>
                      <Checkbox
                        checked={selected.includes(item)}
                        id={`${fieldId}-${item}`}
                        onCheckedChange={(checked) => field.onChange(checked === true ? [...selected, item] : selected.filter((entry) => entry !== item))}
                      />
                      {item}
                    </label>
                  ))}
                </div>
              </div>
            );
          }}
        />
      );
    }

    if (isTextField(key)) {
      // The alpha-2 code keys the record server-side; editing it made legacy /edit
      // fail with "does not exist" — read-only in edit mode (deliberate fix)
      const isReadOnly = key === 'code' && isEditMode;
      return (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={fieldId}>{title}</Label>
          <Input
            className={cn(isReadOnly && 'cursor-not-allowed bg-muted/60 text-muted-foreground')}
            id={fieldId}
            maxLength={TEXT_MAX_LENGTHS[key]}
            readOnly={isReadOnly}
            {...form.register(key)}
          />
          <FieldError message={form.formState.errors[key]?.message} />
        </div>
      );
    }

    return null;
  };

  return (
    <Dialog onOpenChange={(open) => (open ? undefined : onClose())} open>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[700px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="size-5" />
            {isEditMode ? 'Edit Jurisdiction' : 'Add a new Jurisdiction'}
          </DialogTitle>
        </DialogHeader>
        <form className="flex flex-col gap-4" noValidate onSubmit={handleSave}>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {FORM_FIELDS.map((config) => (
              <div className="min-w-0" key={config.key}>
                {renderField(config)}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={onClose} type="button" variant="outline">
              Cancel
            </Button>
            {isEditMode ? (
              <Button onClick={handleDelete} type="button" variant="destructive">
                Delete
              </Button>
            ) : null}
            <Button disabled={!form.formState.isValid} type="submit" variant="primary">
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
JurisdictionFormDialog.displayName = 'JurisdictionFormDialog';
